import { useEffect, useRef } from 'react';

import type { TrackedButtonClickName } from '~/services/gtm.client';

import Tracked from '../../Tracked';

type Props = {
  isExpanded: boolean;
  onSizeSelect: (size?: string) => void;
  selectedSize?: string;
  sizes: string[];
};

export default function SizeSelectorGrid({
  onSizeSelect,
  selectedSize,
  sizes,
  isExpanded
}: Props) {
  return (
    <div
      className={`flex flex-row gap-2 ${isExpanded ? 'flex-wrap' : 'flex-nowrap overflow-scroll pb-5'}`}
    >
      <SizeButton
        name="ALL_SIZES"
        isSelected={!selectedSize}
        onClick={() => onSizeSelect()}
      >
        All
      </SizeButton>
      {sizes.map((size) => (
        <SizeButton
          key={size}
          name={`SIZE_${size}`}
          isSelected={selectedSize === size}
          onClick={() => onSizeSelect(size)}
        >
          {size}
        </SizeButton>
      ))}
    </div>
  );
}

function SizeButton({
  children,
  isSelected,
  name,
  onClick
}: {
  children: string;
  isSelected: boolean;
  name: TrackedButtonClickName;
  onClick: () => void;
}) {
  const baseClassNames =
    'text-sm rounded-3xl border border-grey-divide min-w-[60px] max-w-[72px] p-2 flex-grow basis-0';

  const ref = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isSelected && children !== 'All') {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    }
  }, [isSelected, children]);

  return (
    <Tracked.Button
      name={name}
      variant="none"
      pagePosition="Size Selection"
      className={`${baseClassNames} ${isSelected ? 'bg-black text-white' : 'text-black bg-white'}`}
      onClick={onClick}
    >
      <span ref={ref}>{children}</span>
    </Tracked.Button>
  );
}
